import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Image, Text } from '@chakra-ui/react';
import {
  MainPageHeader,
  Page,
  Loader,
  Discounts,
  StepSelectButton,
} from 'components';
import {
  FirstStepType,
  MasterInfo,
  ServiceGroupsInfo,
  VisitResponse,
  WidgetData,
  Location,
  Alias,
} from 'types';
import { widgetMock } from 'mocks';
import useAxios from 'axios-hooks';
import { useLocation, useParams } from 'react-router-dom';
import { getBaseUrl } from 'api';
import { every, get, keys, some } from 'lodash-es';
import { checkPattern, getMastersSkills } from 'utils';
import { FormPageGroup, FormPageIndividual } from './FormPage';
import { LocalityPage } from './LocalityPage';
import { DivisionPage } from './DivisionPage';
import { useGetAliasByVkParams, useRejectVisit } from '../hooks';
import { EXTERNAL_PROVIDERS, VISIT_TYPES } from '../constants';
import { CancellationSucceedPage } from './CancellationSucceedPage';
import { VisitInfoErrorPage, VisitInfoPage } from './ConfirmPage';
import { VKGroupConnectPage } from './VKGroupConnectPage';

export const MainPage: React.FC = () => {
  const params = useParams<Alias>();
  const [alias, setAlias] = useState(params.alias);
  const location = useLocation();
  const searchParams = useMemo(() => {
    const search = new URLSearchParams(location.search);
    return Object.fromEntries(search.entries());
  }, [location.search]);

  const { visitId } = searchParams;
  const source = checkPattern(searchParams.source, EXTERNAL_PROVIDERS);
  const visitType = checkPattern(searchParams.visitType, VISIT_TYPES);

  // Загрузка данных виджета по alias
  const [{ data = widgetMock, loading, error }] = useAxios<WidgetData>(
    {
      url: `/booking-widget/client/widget/${alias}`,
    },
    { manual: !alias || !!visitId },
  );

  // Загрузка данных о визите
  const [
    { data: visitInfo, loading: loadingVisitInfo, error: visitInfoError },
  ] = useAxios<VisitResponse>(
    {
      url: `/booking-widget/client/visit/${alias}?clientVisitId=${visitId}${
        visitType ? `&visitType=${visitType}` : ''
      }`,
    },
    { manual: !(visitId && alias) },
  );

  // Проверка переданных параметров VK и получение alias по ним
  const { vkDataLoading, vkError, isShowVKGroupConnectPage, isBookingFromVk } =
    useGetAliasByVkParams({
      alias,
      setAlias,
      searchParams,
    });

  // Выбранные пользователем населённый пункт и подразделение
  const [site, setSite] = useState<Location | null>(null);
  const [divisionId, setDivisionId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(
    searchParams.divisionId,
  );
  const [firstStep, setFirstStep] = useState<FirstStepType | null>(null);
  const [response, setResponse] = useState<VisitResponse | null>(null);
  // Для смешанного типа виджета эта переменная определяет идём ли по шагам индивидуальной записи
  const [isIndiWay, setIndiWay] = useState(false);
  const setAndSaveLocality = useCallback(
    (value: Location | null) => {
      setSite(value);
      try {
        if (value?.locality)
          localStorage.setItem(`Locality_${alias}`, value.locality);
        else localStorage.removeItem(`Locality_${alias}`);
        // eslint-disable-next-line no-empty
      } catch {}
    },
    [alias],
  );
  const setAndSaveDivisionId = useCallback(
    (value: string | null) => {
      setDivisionId(value);
      try {
        if (value) localStorage.setItem(`DivisionId_${alias}`, value);
        else localStorage.removeItem(`DivisionId_${alias}`);
        // eslint-disable-next-line no-empty
      } catch {}
    },
    [alias],
  );
  const isManual = !(
    data.id &&
    ((data.division && divisionId) || !data.division)
  );
  const visit = response || visitInfo;
  const {
    isRejectAllowed,
    success: isRejectSuccess,
    error: isRejectFailed,
    loading: isRejectLoading,
    rejectVisit,
    resetError: resetRejectError,
  } = useRejectVisit({
    rejectAllowedForWidget: visit?.rejectAllowed,
    visitId: visit?.id,
    divisionId: divisionId || visitInfo?.divisionId,
    durationRejectConstraint:
      data.durationRejectConstraint || visitInfo?.durationRejectConstraint,
    date: visit?.date,
  });

  const [
    { data: mastersData = [], loading: mastersLoading, error: mastersError },
  ] = useAxios<MasterInfo[]>(
    {
      url: `/booking-widget/client/widget/${data.id}/employee`,
      params: {
        ...(divisionId ? { divisionId } : {}),
      },
    },
    { manual: isManual },
  );

  const [
    { data: servicesData = {}, loading: servicesLoading, error: servicesError },
  ] = useAxios<ServiceGroupsInfo>(
    {
      url: `/booking-widget/client/widget/${data.id}/favor`,
      params: {
        ...(divisionId ? { divisionId } : {}),
      },
    },
    { manual: isManual },
  );

  const mastersWithSkills = useMemo(
    () =>
      mastersData.filter((master) =>
        keys(getMastersSkills(servicesData)).includes(master.id),
      ),
    [servicesData, mastersData],
  );

  // Пропускаем шаг "Выбор мастера", если мастера "скрыты" в настройках виджета
  const isSetAnyEmployee = useMemo(() => {
    return (
      every(mastersData, (master) => master.isShow === false) &&
      some(mastersData, 'anyEmployee')
    );
  }, [mastersData]);

  const localities = useMemo(() => {
    return data.division?.length
      ? data.division.map(({ locality }) => ({ locality }))
      : undefined;
  }, [data]);

  const divisions = useMemo(() => {
    return data.division?.length
      ? data.division.find((dvn) => dvn.locality === site?.locality)
      : undefined;
  }, [data, site]);

  const division = useMemo(() => {
    return divisions
      ? divisions.item.find((dvn) => dvn.id === divisionId)
      : null;
  }, [divisions, divisionId]);

  const foundLocalityForBranchId = useMemo(() => {
    return branchId && data.division
      ? data.division.find((loc) => loc.item.find((dvn) => dvn.id === branchId))
      : null;
  }, [data.division, branchId]);

  // Установка divisionId из useSearchParams, если он актуален
  useEffect(() => {
    if (branchId && foundLocalityForBranchId) {
      const { locality } = foundLocalityForBranchId;
      setSite({ locality });
      setDivisionId(branchId);
    }
  }, [branchId, foundLocalityForBranchId]);

  // Установка локации, если она одна
  useEffect(() => {
    if (!branchId && localities?.length === 1) {
      setAndSaveLocality(localities[0]);
    }
  }, [branchId, localities, setAndSaveLocality]);

  // Установка филиала, если он один
  useEffect(() => {
    if (!branchId && divisions?.item.length === 1) {
      setAndSaveDivisionId(divisions.item[0].id);
    }
  }, [branchId, divisions, setAndSaveDivisionId]);

  // Чтение locality и divisionId из localStorage и установка их, если они актуальны
  useEffect(() => {
    if (branchId) return;
    const savedLocality = localStorage.getItem(`Locality_${alias}`);
    const savedDivisionId = localStorage.getItem(`DivisionId_${alias}`);
    if (savedLocality) {
      const foundLocality = data.division?.find(
        (dvn) => dvn.locality === savedLocality,
      );

      if (foundLocality) {
        const { locality } = foundLocality;
        setSite({ locality });
      }
      if (
        savedDivisionId &&
        foundLocality?.item.find((dvn) => dvn.id === savedDivisionId)
      ) {
        setDivisionId(savedDivisionId);
      }
    }
  }, [alias, branchId, data.division]);

  if (!branchId && site === null && localities) {
    return (
      <LocalityPage
        data={data}
        loading={loading}
        error={error || !alias}
        localities={localities}
        setLocality={setAndSaveLocality}
      />
    );
  }

  if (!branchId && !divisionId && divisions && site !== null) {
    return (
      <DivisionPage
        data={data}
        loading={loading}
        error={error || !alias}
        locality={site.locality}
        divisions={divisions.item}
        backToLocality={!!(site && localities && localities.length > 1)}
        setLocality={setAndSaveLocality}
        setDivision={setAndSaveDivisionId}
      />
    );
  }

  const {
    specialistNominativeCase,
    specialistGenitiveCase,
    specialistPrepositionalCase,
    fieldNameLabel,
    fieldCommentLabel,
  } = data.controlSettings?.fields || widgetMock.controlSettings.fields;
  const formData = {
    mastersData: mastersWithSkills,
    servicesData,
    hasRecordingFromService: data.hasRecordingFromService,
    hasRecordingFromMaster: data.hasRecordingFromMaster,
    hasRecordingFromDate: data.hasRecordingFromDate,
    specialistCases: {
      specialistNominativeCase,
      specialistGenitiveCase,
      specialistPrepositionalCase,
    },
    additionalPersonsAllowed: data.additionalPersonsAllowed,
    fieldNameLabel,
    fieldCommentLabel,
    maxVisitDuration: data.maxVisitDuration,
  };
  const formLoading = mastersLoading || servicesLoading;
  const formError = mastersError || servicesError;

  const addressTitle = division?.address
    ? division.address
    : data.company.address;

  const homeLinkWithSource = source
    ? `/${alias}?source=${source}`
    : `/${alias}`;
  const divisionIdParam = foundLocalityForBranchId
    ? `?divisionId=${divisionId}`
    : '';
  const homeLink = homeLinkWithSource + divisionIdParam;

  if (isRejectSuccess) {
    return <CancellationSucceedPage homeLink={homeLink} />;
  }

  if (visitInfoError) {
    return (
      <VisitInfoErrorPage
        homeLink={homeLink}
        status={get(visitInfoError, 'response.status')}
      />
    );
  }

  if (visit) {
    return (
      <VisitInfoPage
        companyName={visit.company?.name || data.company.name}
        address={visit.company?.address || addressTitle}
        response={visit}
        homeLink={homeLink}
        isRejectAllowed={isRejectAllowed}
        cancelVisit={rejectVisit}
        // eslint-disable-next-line prettier/prettier
        statusInfo={(
          <Loader
            loading={isRejectLoading}
            error={isRejectFailed}
            errorMessage="Попробуйте еще раз"
            onErrorClose={resetRejectError}
            opacity={isRejectLoading ? 0.9 : 1}
          />
          // eslint-disable-next-line prettier/prettier
        )}
      />
    );
  }

  if (isShowVKGroupConnectPage) {
    return <VKGroupConnectPage />;
  }

  const {
    nameButtonService = 'Выбрать услугу',
    nameButtonMaster = `Выбрать ${specialistGenitiveCase}`,
    nameButtonDate = 'Выбрать дату',
    nameButtonAppointment = 'Записаться',
  } = data.controlSettings?.buttons || {};

  if (firstStep) {
    const FormPage = firstStep === 'group' ? FormPageGroup : FormPageIndividual;
    return (
      <FormPage
        initialValues={{
          widgetId: data.id,
          employeeId:
            isSetAnyEmployee && firstStep !== 'master' ? 'any' : undefined,
          clientInfo:
            firstStep === 'group'
              ? {
                  personsCount: 1,
                }
              : undefined,
        }}
        firstStep={firstStep}
        address={addressTitle}
        onFinish={setResponse}
        onExit={() => setFirstStep(null)}
        dataLoading={formLoading}
        dataError={formError}
        data={formData}
        duration={data.duration}
        divisionId={divisionId}
        timezone={division?.timezone}
        nameButtonService={nameButtonService}
        nameButtonMaster={nameButtonMaster}
        isBookingFromVk={isBookingFromVk}
      />
    );
  }

  const has = [
    data.hasRecordingFromMaster,
    data.hasRecordingFromService,
    data.hasRecordingFromDate,
  ];
  const lengthHas = has.filter((item) => item)?.length;
  const isGroupWidget = data.widgetType === 'group';
  const isShowIndiButtons = data.widgetType === 'individual' || isIndiWay;

  const backFromMainPage = () => {
    if (isIndiWay) return () => setIndiWay(false);
    if (divisionId && divisions && divisions.item.length > 1)
      return () => {
        setAndSaveDivisionId(null);
        if (branchId) setBranchId(null);
      };
    return site && localities && localities.length > 1
      ? () => {
          setAndSaveLocality(null);
          setAndSaveDivisionId(null);
          if (branchId) setBranchId(null);
        }
      : undefined;
  };

  const renderButtons = () => {
    if (isShowIndiButtons) {
      return (
        <>
          {data.hasRecordingFromService && (
            <StepSelectButton
              variant="outline"
              width={{
                base: '100%',
                md: lengthHas === 3 ? '30%' : '268px',
              }}
              mr={lengthHas === 2 ? { md: 8 } : undefined}
              onClick={() => setFirstStep('service')}
            >
              {nameButtonService}
            </StepSelectButton>
          )}
          {data.hasRecordingFromMaster && (
            <StepSelectButton
              variant="outline"
              width={{
                base: '100%',
                md: lengthHas === 3 ? '30%' : '268px',
              }}
              mt={{ base: 2, md: 0 }}
              mr={lengthHas === 2 ? { md: 8 } : undefined}
              onClick={() => setFirstStep('master')}
            >
              {nameButtonMaster}
            </StepSelectButton>
          )}
          {data.hasRecordingFromDate && (
            <StepSelectButton
              variant="outline"
              width={{
                base: '100%',
                md: lengthHas === 3 ? '30%' : '268px',
              }}
              mt={{ base: 2, md: 0 }}
              onClick={() => setFirstStep('date')}
            >
              {nameButtonDate}
            </StepSelectButton>
          )}
        </>
      );
    }
    if (isGroupWidget) {
      return (
        <StepSelectButton
          variant="outline"
          width={{ base: '100%', md: '268px' }}
          onClick={() => setFirstStep('group')}
        >
          {nameButtonAppointment}
        </StepSelectButton>
      );
    }
    return (
      <>
        <StepSelectButton
          variant="outline"
          width={{
            base: '100%',
            md: '268px',
          }}
          mr={{ md: 8 }}
          onClick={() => setIndiWay(true)}
        >
          Индивидуальная запись
        </StepSelectButton>
        <StepSelectButton
          variant="outline"
          width={{
            base: '100%',
            md: '268px',
          }}
          mt={{ base: 2, md: 0 }}
          onClick={() => setFirstStep('group')}
        >
          Групповые события
        </StepSelectButton>
      </>
    );
  };

  return (
    <Page
      // eslint-disable-next-line prettier/prettier
      title={(
        <MainPageHeader
          name={data.company.name}
          type={data.company.type}
          address={addressTitle}
        />
        // eslint-disable-next-line prettier/prettier
      )}
      onBackClick={backFromMainPage()}
    >
      <Loader
        loading={loading || vkDataLoading || loadingVisitInfo}
        error={
          error ||
          vkError ||
          formError ||
          !alias ||
          visitInfoError ||
          typeof foundLocalityForBranchId === 'undefined'
        }
        astro={typeof foundLocalityForBranchId === 'undefined'}
      />
      <Box>
        {data.showBanner && (
          <Discounts
            widgetId={data.id}
            divisionId={divisionId}
            loadDiscountsManually={isManual}
          />
        )}
        {(!!division?.pictureId || !!data.company.pictureId) && (
          <Image
            w="100%"
            maxH="306px"
            objectFit="cover"
            borderRadius={{
              base: '8px',
              md: '20px',
            }}
            src={`${getBaseUrl()}/booking-widget/client/attachment/file/${
              division?.pictureId || data.company.pictureId
            }/inline`}
            alt="Фото"
            loading="eager"
          />
        )}
        <Box w="100%">
          <Text
            color="black"
            m="4"
            textStyle="body1"
            whiteSpace="pre-wrap"
            translate="no"
          >
            {division?.description || data.company.description}
          </Text>
        </Box>
        <Box
          display={
            isShowIndiButtons && lengthHas === 3 ? { md: 'flex' } : undefined
          }
          justifyContent={
            isShowIndiButtons && lengthHas === 3
              ? { md: 'space-between' }
              : undefined
          }
          textAlign="center"
          mt={{ base: '2', xs: '4', md: '44px' }}
        >
          {renderButtons()}
        </Box>
      </Box>
    </Page>
  );
};
